<script setup lang="ts">
import {computed, reactive, ref, useTemplateRef, watch} from "vue";
import {Comments, PageLoading, PageTitle} from "@/Components/Global";
import {AutoComplete, Button, Label, Menu, Drawer} from "@/Components/Library";
import {useRoute} from "vue-router";
import {getGroups} from "@/Services/groups";
import {getQuestion} from "@/Services/questions";
import {getQuestionResponses} from "@/Services/responses";
import {getQuestionComments} from "@/Services/comments";
import {analyzeQuestion} from "@/Services/analysis";
import StimulusDisplay from "../Stimuli/StimulusDisplay.vue";
import QuestionResponsesTable from "@/Pages/User/Questions/Question/Responses/QuestionResponsesTable.vue";
import QuestionResponsesChart from "@/Pages/User/Questions/Question/Responses/QuestionResponsesChart.vue";
import QuestionResponsesWordCloud from "@/Pages/User/Questions/Question/Responses/QuestionResponsesWordCloud.vue";
import QuestionResponsesVoting from "@/Pages/User/Questions/Question/Responses/QuestionResponsesVoting.vue";
import Question from "@/Models/Question";
import axios from "axios";
import {useToast} from "primevue/usetoast";
import DatePicker from "primevue/datepicker";
import dayjs from "dayjs";
import ResizableColumns from "@/Components/Global/ResizableColumns.vue";
import StimulusBlock from "@/Pages/User/Questions/Question/StimulusBlock.vue";
import {breakpointsTailwind, useBreakpoints} from "@vueuse/core/index";
import {useMainStore} from "@/Stores/store.ts";
import User from "@/Models/User";
import AnalyzeResponsesDrawer from "@/Pages/User/Responses/AnalyzeResponsesDrawer.vue";

const $route = useRoute();
const $user = new User();
const $store = useMainStore();
const toast = useToast();
const breakpoints = useBreakpoints(breakpointsTailwind);

const state = reactive({
    show: {
        group: null,
        question: null,
        responseForm: false,
        stimulusDetails: false,
        analysisModal: false,
    },
    filter: {
        group: null,
        dateFrom: null,
        dateTo: null,
    },
    //TODO sort out loading states
    loading: {
        question: true,
        groups: true,
        responses: true,
        comments: true,
        export: false,
    },
});
const question = ref<Question | null>(null);
const groups = ref([]);
const selectedGroups = ref(groups.value);

getGroups({filter: {question: $route.params.question}}).then(({data}) => {
    groups.value = data;
    selectedGroups.value = data;
    state.loading.groups = false;
});

// TODO: 'preview' here is temporary to not load responses, but also not to break other question pages logic before a proper refactor
getQuestion($route.params.question, {preview: true}).then(({data}) => {
    question.value = new Question(data);
    state.loading.question = false;
    getResponses();
    getComments();
    if ((question.value.is("text") && question.value.param("allow_annotations")) || question.value.is("annotation")) {
        exportMenuItems.value.push(
            {
                label: "PDF - Landscape",
                icon: "mdi:file-pdf-box",
                command: () => {
                    exportResponsesData("pdf", "landscape");
                },
            },
            {
                label: "PDF - Portrait",
                icon: "mdi:file-pdf-box",
                command: () => {
                    exportResponsesData("pdf", "portrait");
                },
            },
        );
    }
});

const getResponses = async () => {
    return getQuestionResponses($route.params.question, {
        include: [
            "group",
            "user.profile.avatars",
            "comments.group",
            "comments.commentator.profile.avatars",
            "comments.reactions",
            "comments.comments.commentator.profile.avatars",
            "commentsCount",
        ],
        filter: {
            group: state.filter.group,
            date_from: state.filter.dateFrom ? dayjs(state.filter.dateFrom).utc().format("YYYY-MM-DD HH:mm:ss") : null,
            date_to: state.filter.dateTo ? dayjs(state.filter.dateTo).utc().format("YYYY-MM-DD HH:mm:ss") : null,
        },
    }).then(({data}) => {
        question.value.question.responses = data;
        state.loading.responses = false;
    });
};

const getComments = async () => {
    return getQuestionComments($route.params.question, {
        include: ["group", "commentator.profile.avatars", "comments.commentator.profile.avatars"],
        filter: {
            groups: state.filter.group,
            date_from: state.filter.dateFrom ? dayjs(state.filter.dateFrom).format("YYYY-MM-DD HH:mm:ss") : null,
            date_to: state.filter.dateTo ? dayjs(state.filter.dateTo).format("YYYY-MM-DD HH:mm:ss") : null,
        },
    }).then(({data}) => {
        question.value.question.comments = data;
        state.loading.comments = false;
    });
};

const sort = ref(question.value?.stimuli?.[0]?.files?.length > 1 ? "page" : "votes");
const sortPanel = useTemplateRef("sortPanel");
const exportPanel = useTemplateRef("exportPanel");
const sortByPageMenuItems = {
    label: "By page",
    icon: "mdi:sort-numeric-descending-variant",
    command: () => {
        sort.value = "page";
    },
};
const sortMenuItemsBase = [
    {
        label: "Most votes",
        icon: "mdi:sort-numeric-descending",
        command: () => {
            sort.value = "votes";
        },
    },
    {
        label: "Most comments",
        icon: "mdi:comment-multiple",
        command: () => {
            sort.value = "comments";
        },
    },
    {
        label: "By person",
        icon: "mdi:account",
        command: () => {
            sort.value = "person";
        },
    },
    {
        label: "Recently edited",
        icon: "ic:round-edit-calendar",
        command: () => {
            sort.value = "date";
        },
    },
];
const sortMenuItems = ref([]);
const exportMenuItems = ref([
    {
        label: "CSV",
        icon: "mdi:file-delimited-outline",
        command: () => {
            exportResponsesData("csv");
        },
    },
]);

const openSortMenu = (event: any) => {
    if (question.value?.is("text") && question.value?.param("allow_annotations") && question.value?.pageCount > 1) {
        sortMenuItems.value = [sortByPageMenuItems, ...sortMenuItemsBase];
    } else {
        sortMenuItems.value = sortMenuItemsBase;
    }

    sortPanel.value.menu.toggle(event);
};

const openExportMenu = (event: any) => {
    exportPanel.value.menu.toggle(event);
};

const points = computed(() => question.value.responses.filter((A) => A.data.annotation).map((A) => A.data.annotation));
const wordcloud = useTemplateRef("wordcloud");
const stimulusBlock = useTemplateRef("stimulusBlock");
const stimulusDisplay = useTemplateRef("stimulusDisplay");
const responsesTable = useTemplateRef("responsesTable");
const annotation = ref();
const pageIndex = ref(0);

const hasFilters = computed(() => {
    return !!state.filter.group?.length || !!state.filter.dateFrom || !!state.filter.dateTo;
});

const responseClick = async (id) => {
    if (!["image", "slides"].includes(question.value.stimuli?.[0].type.slug) || !question.value.is("text")) {
        return;
    }
    const response = question.value.response(id);
    if (!response.data.annotation) {
        return;
    }
    pageIndex.value = +response.data.annotation?.pageIndex;
    let block = breakpoints.greaterOrEqual("xl").value ? stimulusBlock : stimulusBlockSm;
    block.value.stimulusDisplay.swiper?.swiper.slideTo(+response.data.annotation.pageIndex);
    // console.log(id, response, pageIndex.value, stimulusBlock.value.stimulusDisplay);
    points.value.forEach((A) => (A.focussed = false));
    responsesTable.value.responseRefs.forEach((A) => {
        A.content.classList.remove("bg-askr-green-50/20", "border-askr-green", "dark:border-askr-green");
        A.content.classList.add("dark:border-stone-700");
    });
    console.log(breakpoints.greaterOrEqual("xl"), block);
    setTimeout(() => {
        const point = block.value.stimulusDisplay.pointRefs?.find((A) => A.id === response.data.annotation.id);
        console.log(point);
        if (point) {
            const annotation = points.value.find((A) => A.id === response.data.annotation.id);
            annotation.focussed = true;
            point.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
            const responseContainer = responsesTable.value.responseRefs.find((A) => A.content.id === response.id);
            responseContainer.content.classList.remove("dark:border-stone-700");
            responseContainer.content.classList.add(
                "bg-askr-green-50/20",
                "border-askr-green",
                "dark:border-askr-green",
            );
        }
    }, 0);
};

const pointClick = (point) => {
    console.log(point, question.value.responses);
    responsesTable.value.responseRefs.forEach((A) => console.log(A.content));
    const response = question.value.response(point.id);
    console.log(response);
    // return;
    annotation.value = null;
    if (!response) {
    }
    points.value.forEach((A) => (A.focussed = false));
    responsesTable.value.responseRefs.forEach((A) => {
        A.content.classList.remove("bg-askr-green-50/20", "border-askr-green", "dark:border-askr-green");
        A.content.classList.add("dark:border-stone-700");
    });
    point.focussed = true;
    // responsesTable.value.responseRefs.forEach((A) => console.log(A.content.id === response.id));
    const responseContainer = responsesTable.value.responseRefs.find((A) => A.content.id === response.id);
    console.log(responseContainer);
    if (responseContainer?.content) {
        responseContainer.content.scrollIntoView({behavior: "smooth", block: "start"});
        responseContainer.content.classList.remove("dark:border-stone-700");
        responseContainer.content.classList.add("bg-askr-green-50/20", "border-askr-green", "dark:border-askr-green");
    }
    // console.log(
    //     point,
    //     response,
    //     responsesTable.value.responseRefs.find((A) => A.id === `a${response.id}`),
    // );
};

const clearPointsFocus = () => {
    console.log("clear");
    points.value.forEach((A) => (A.focussed = false));
    responsesTable.value?.responseRefs?.forEach((A) => {
        A?.content?.classList?.remove("bg-askr-green-50/20", "border-askr-green", "dark:border-askr-green");
        A.content.classList.add("dark:border-stone-700");
    });
};

watch(() => stimulusDisplay.value?.activeIndex, clearPointsFocus);
watch(selectedGroups, async (val) => {
    state.filter.group = val.length ? val.map((A) => A.id) : null;
    await getResponses();
    await getComments();
});

watch(
    () => state.filter,
    async () => {
        await getResponses();
        await getComments();
    },
    {deep: true},
);

const exportResponsesData = async (format = "csv", orientation = "landscape") => {
    try {
        state.loading.export = true;

        const response = await axios.get(
            route(format === "pdf" ? "questions.export-pdf" : "questions.export", $route.params.question),
            {
                params: {
                    group_ids: selectedGroups.value.length ? selectedGroups.value.map((group) => group.id) : null,
                    date_from: state.filter.dateFrom
                        ? dayjs(state.filter.dateFrom).utc().format("YYYY-MM-DD HH:mm:ss")
                        : null,
                    date_to: state.filter.dateTo
                        ? dayjs(state.filter.dateTo).utc().format("YYYY-MM-DD HH:mm:ss")
                        : null,
                    orientation: format === "pdf" ? orientation : null,
                },
            },
        );

        window.location.href = route("files.download", response.data.path);

        toast.add({
            severity: "success",
            summary: "Success",
            detail: `Question responses exported as ${format.toUpperCase()} successfully.`,
            life: 3000,
        });
    } catch (error) {
        console.error(error);
        toast.add({
            severity: "error",
            summary: "Error",
            detail: `Failed to export question responses as ${format.toUpperCase()}.`,
            life: 3000,
        });
    } finally {
        state.loading.export = false;
    }
};
</script>
<template>
    <div class="m-container bg--white mx-auto mb-8" @click="clearPointsFocus">
        <div class="mb-3" :class="[{'mx-auto max-w-7xl': !question?.stimulus}]">
            <PageTitle title="Responses">
                <p v-if="!!question?.stimulus" class="ml-auto hidden text-xs text-stone-500 xl:block">
                    Click and drag the dotted line to adjust the column widths
                </p>
            </PageTitle>
        </div>
        <PageLoading v-if="state.loading.question" class="pt-16" />
        <div v-if="question">
            <div class="w-full">
                <ResizableColumns>
                    <template #left>
                        <div
                            class="top-4 mx-auto mb--4 max-w-7xl pr-2"
                            :class="[
                                {
                                    'xl:sticky xl:max-h-[calc(100vh-theme(space.8))] xl:overflow-y-auto':
                                        !!question.stimulus,
                                },
                            ]"
                        >
                            <div class="mb-4">
                                <h3 class="mb-2 text-xl font-semibold leading-normal">
                                    {{ question.get("title") }}
                                </h3>
                                <!--                <TagList class="mt-2" :tags="question.tags" />-->
                            </div>
                            <div class="mb-4">
                                <Label class="mb-1" for="groups_selector">Groups to display responses from</Label>
                                <AutoComplete
                                    input-id="groups_selector"
                                    multiple
                                    :items="groups"
                                    v-model="selectedGroups"
                                    label="Filter by group"
                                    dropdown
                                    forceSelection
                                    fluid
                                    size="large"
                                />
                            </div>
                            <div class="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div>
                                    <Label for="date_from" class="mb-1">From Date</Label>
                                    <DatePicker
                                        id="date_from"
                                        v-model="state.filter.dateFrom"
                                        placeholder="Select start date"
                                        showTime
                                        fluid
                                        date-format="dd/mm/yy"
                                        hour-format="24"
                                        :hideOnDateTimeSelect="true"
                                    />
                                </div>
                                <div>
                                    <Label for="date_to" class="mb-1">To Date</Label>
                                    <DatePicker
                                        id="date_to"
                                        v-model="state.filter.dateTo"
                                        placeholder="Select end date"
                                        showTime
                                        :minDate="state.filter.dateFrom"
                                        fluid
                                        date-format="dd/mm/yy"
                                        hour-format="24"
                                        :hideOnDateTimeSelect="true"
                                    />
                                </div>
                            </div>
                            <div class="mb-4 flex justify-center gap-2">
                                <Button
                                    v-if="$user.hasRole('super-admin')"
                                    class="bg-askr-green text-white"
                                    @click.prevent.stop="state.show.analysisModal = true"
                                    :icon="{icon: 'prime:sparkles', width: 20}"
                                >
                                    Analyze
                                </Button>
                                <Button
                                    v-if="$user.can('export data')"
                                    class="bg-askr-green text-white"
                                    @click.prevent.stop="openExportMenu"
                                    :loading="state.loading.export"
                                    :icon="{icon: 'prime:download', width: '24'}"
                                >
                                    Export
                                </Button>
                                <Menu ref="exportPanel" :popup="true" :model="exportMenuItems" />
                            </div>
                            <template v-if="state.loading.responses || state.loading.comments">
                                <PageLoading class="pt-16" />
                            </template>
                            <template v-else>
                                <template v-if="!question.is('post')">
                                    <div v-if="question.is('text')" class="mb-8">
                                        <div class="-mb-4 flex items-start justify-between">
                                            <h3 class="font-bold text-stone-500 lg:px-0">Responses</h3>
                                            <Button
                                                :icon="{icon: 'iconoir:sort', width: 26}"
                                                :rounded="false"
                                                class="aspect-square h-full flex-none bg-askr-green !p-2.5 text-white hover:brightness-110"
                                                @click="openSortMenu"
                                            />
                                            <Menu ref="sortPanel" :popup="true" :model="sortMenuItems" />
                                        </div>
                                        <QuestionResponsesVoting
                                            ref="responsesTable"
                                            class="mb-6 overflow-y-auto"
                                            :question="question"
                                            :sort="sort"
                                            :has-filters="hasFilters"
                                            @responseClick="responseClick"
                                            disabled
                                        />
                                    </div>
                                </template>
                                <template v-if="!question.is(['text', 'post'])">
                                    <h3 class="mb-1 font-bold text-stone-500 lg:px-0">Responses</h3>
                                    <div class="">
                                        <QuestionResponsesTable
                                            :question="question"
                                            :has-filters="hasFilters"
                                            @response-click="responseClick"
                                        />
                                    </div>
                                </template>
                                <div class="mb-6">
                                    <h3 class="mb-1 font-bold text-stone-500 lg:px-0">Comments</h3>
                                    <div>
                                        <Comments
                                            :model="question"
                                            :showForm="false"
                                            disabled
                                            model-type="question"
                                            :has-filters="hasFilters"
                                        />
                                    </div>
                                </div>
                                <template v-if="!question.is(['voice', 'text', 'post'])">
                                    <h3 class="mb-1 font-bold text-stone-500 lg:px-0">Charts</h3>
                                    <div class="mb-6">
                                        <QuestionResponsesChart
                                            v-if="question.is(['multiple_choice', 'ranking', 'slider', 'weighting'])"
                                            :question="question"
                                            :has-filters="hasFilters"
                                        />
                                        <QuestionResponsesWordCloud
                                            ref="wordcloud"
                                            :question="question"
                                            v-if="question.is('word')"
                                            :has-filters="hasFilters"
                                        />
                                    </div>
                                </template>
                            </template>
                        </div>
                    </template>
                    <template #right v-if="question.stimulus">
                        <StimulusBlock
                            class="hidden xl:block"
                            ref="stimulusBlock"
                            :question="question"
                            :points="points"
                            :annotation="annotation"
                            @pointClick="pointClick"
                        />
                    </template>
                </ResizableColumns>
            </div>
        </div>
        <AnalyzeResponsesDrawer
            v-model:visible="state.show.analysisModal"
            :question="question"
            :selected-groups="selectedGroups"
            :date-from="state.filter.dateFrom"
            :date-to="state.filter.dateTo"
        />
    </div>
</template>
<style scoped></style>
